import React, { useCallback, useContext, useMemo } from 'react';
import Thumbnail from '@amzn/meridian/thumbnail';
import './AssociateViewTable.css';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import { useRef, useState } from 'react';
import Popover from '@amzn/meridian/popover';
import Select, { SelectOption } from '@amzn/meridian/select';
import Icon from '@amzn/meridian/icon';
import errorKnockoutIcon from '@amzn/meridian-tokens/base/icon/error-knockout';
import { ShiftAllocationContext } from '../context/ShiftAllocationContext';
import { hasPlacementIssues, getAislesOptionsForBoardEntry } from './utils';
import { EMPLOYEE_ICON_COLORS, PROCESS_PATHS, ALLOCATION_TYPE } from '../data';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Badge from '@mui/material/Badge';
import { getRegion } from '../../utils/helpers';
import { BADGE_PHOTO_URL } from '../../utils/constants';
import { EditableLabel } from './EditableLabel';

export const EmployeeIcon = ({
  boardEntry,
  associate,
  assignedProcess,
  isBenched,
  header,
  editableHeader,
  onEditHeader
}) => {
  const buttonRef = useRef();
  const [openPopOver, setOpenPopOver] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  // For aisle destinations: the user-chosen aisle span (free min/max, not limited to chunks).
  const [minAisle, setMinAisle] = useState(null);
  const [maxAisle, setMaxAisle] = useState(null);
  const { shiftAllocationModel, shiftAllocationDispatch, setAvailableAssociatesVisibility, setIsBoardUpdated } =
    useContext(ShiftAllocationContext);

  const onClickEmployeeIcon = () => {
    setOpenPopOver(true);
  };

  const onClose = () => {
    setOpenPopOver(false);
    // Reset click-to-assign selections so a dismissed (un-assigned) popover does not
    // reopen with stale picks, or a destination that no longer exists on the board.
    setSelectedDestination(null);
    setMinAisle(null);
    setMaxAisle(null);
  };

  const actualHeader = useMemo(() => {
    return header || assignedProcess;
  }, [header, assignedProcess]);

  const photoUrl = useMemo(() => {
    const regex = /^\d+$/;
    if (regex.test(associate.alias))
      return `${BADGE_PHOTO_URL[getRegion().toUpperCase()]}/?employeeid=${associate.alias}`;
    return `${BADGE_PHOTO_URL[getRegion().toUpperCase()]}/?uid=${associate.alias}`;
  }, [associate]);

  const associatePlacementIssueStatus = useMemo(() => {
    if (!assignedProcess) {
      return {
        hasIssue: false
      };
    }
    return hasPlacementIssues(associate, assignedProcess, boardEntry);
  }, [boardEntry, associate, assignedProcess]);

  const borderStyle = useMemo(() => {
    const style = { border: '2px ' + 'Solid' + ' ' + `${EMPLOYEE_ICON_COLORS.CLOCKED_IN}` };
    if (associatePlacementIssueStatus.hasIssue) {
      style.border = '2px ' + 'Solid' + ' ' + `${EMPLOYEE_ICON_COLORS.ISSUE}`;
    } else if (!associate.clockedInStatus) {
      style.border = '2px ' + 'Dashed' + ' ' + `${EMPLOYEE_ICON_COLORS.CLOCKED_OUT}`;
    }
    return style;
  }, [associate, associatePlacementIssueStatus]);

  const ProcessPathListView = ({ processPathList, title }) => {
    return (
      <Row spacing="100">
        <Column alignmentVertical="top" heights={['fill']}>
          <Text type="h100" style={{ height: '100%' }}>
            {title}:
          </Text>
        </Column>
        <Column>
          <Row wrap="down" spacing="none">
            {processPathList.map((path, index) => (
              <React.Fragment key={path}>
                <Text type="b300" color={'secondary'}>
                  {path}
                </Text>
                {index < processPathList.length - 1 && <Text>, </Text>}
              </React.Fragment>
            ))}
          </Row>
        </Column>
      </Row>
    );
  };

  const findReplacement = useCallback(() => {
    setAvailableAssociatesVisibility(true);
  }, []);

  const remove = useCallback(() => {
    setIsBoardUpdated(true);
    shiftAllocationDispatch({
      type: 'MOVE_ASSOCIATE',
      data: {
        associateAlias: associate.alias,
        from: {
          boardEntry: boardEntry.id,
          processPathId: assignedProcess
        },
        to: {
          boardEntry: PROCESS_PATHS.BENCH
        }
      }
    });
  }, [shiftAllocationDispatch, boardEntry, assignedProcess, associate]);

  const trackingBadge = associate.trackingPath ? (
    <span
      style={{
        position: 'absolute',
        top: -2,
        left: -2,
        width: 14,
        height: 14,
        borderRadius: '50%',
        backgroundColor: '#348118',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1
      }}>
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2 6.5L5 9.5L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ) : null;
  // Build the list of click-to-assign destinations from the current board.
  // Each option is a (boardEntry, processPath) pair. Bench/5S are excluded.
  // Only process paths the associate is TRAINED on are offered, matched case-insensitively
  // against associate.trainedPaths (same convention as hasPlacementIssues). An associate with
  // no trained paths gets no destinations and falls through to the empty-state message.
  const assignDestinations = useMemo(() => {
    const processInfo = (shiftAllocationModel && shiftAllocationModel.processInfo) || [];
    const trainedPaths = ((associate && associate.trainedPaths) || []).map((p) => p.toLowerCase());
    const destinations = [];
    processInfo.forEach((entry) => {
      if (entry.id === PROCESS_PATHS.BENCH || entry.id === PROCESS_PATHS.FIVE_S) {
        return;
      }
      const isAisleEntry = entry.allocationType === ALLOCATION_TYPE.AISLES;
      const processPathEntries = entry.processPathEntries || [];
      if (processPathEntries.length === 0) {
        return;
      }
      processPathEntries.forEach((ppe) => {
        // Only surface destinations whose process path the associate is trained on.
        if (!ppe.process || !trainedPaths.includes(ppe.process.toLowerCase())) {
          return;
        }
        const value = `${entry.id}::${ppe.process}`;
        const label = processPathEntries.length > 1 ? `${entry.title} — ${ppe.process}` : entry.title;
        // isAisle destinations need an aisle span (min/max) before assigning.
        destinations.push({ value, label, isAisle: isAisleEntry });
      });
    });
    return destinations.sort((a, b) => a.label.localeCompare(b.label));
  }, [shiftAllocationModel, associate]);

  // The currently-selected destination's metadata (whether it's an aisle entry, its board entry, and process).
  const selectedDestinationMeta = useMemo(() => {
    if (!selectedDestination) {
      return null;
    }
    const [toBoardEntryId, toProcessPath] = selectedDestination.split('::');
    const dest = assignDestinations.find((d) => d.value === selectedDestination);
    const processInfo = (shiftAllocationModel && shiftAllocationModel.processInfo) || [];
    const entry = processInfo.find((e) => e.id === toBoardEntryId);
    return { isAisle: Boolean(dest && dest.isAisle), entry, processPath: toProcessPath };
  }, [selectedDestination, assignDestinations, shiftAllocationModel]);

  // Full list of selectable aisle numbers for the selected aisle destination — the complete
  // cluster range (e.g. 3..20), NOT limited to currently-open chunks. The planner picks any
  // min/max span; the reducer expands min..max into aisles and the board reflects it.
  const aisleOptions = useMemo(() => {
    if (!selectedDestinationMeta || !selectedDestinationMeta.isAisle || !selectedDestinationMeta.entry) {
      return [];
    }
    const clusterDefinitions = (shiftAllocationModel && shiftAllocationModel.clusterDefinitions) || [];
    return getAislesOptionsForBoardEntry(selectedDestinationMeta.entry, clusterDefinitions);
  }, [selectedDestinationMeta, shiftAllocationModel]);

  // When a destination is picked, reset the aisle span.
  const onChangeDestination = useCallback((value) => {
    setSelectedDestination(value);
    setMinAisle(null);
    setMaxAisle(null);
  }, []);

  // Assign is allowed when a destination is chosen; aisle destinations additionally require a
  // valid span (both ends chosen, min <= max).
  const isAssignDisabled = useMemo(() => {
    if (!selectedDestination) {
      return true;
    }
    if (selectedDestinationMeta && selectedDestinationMeta.isAisle) {
      return minAisle == null || maxAisle == null || Number(minAisle) > Number(maxAisle);
    }
    return false;
  }, [selectedDestination, selectedDestinationMeta, minAisle, maxAisle]);

  const onClickAssign = useCallback(() => {
    if (!selectedDestination) {
      return;
    }
    const [toBoardEntry, toProcessPath] = selectedDestination.split('::');
    const isAisle = selectedDestinationMeta && selectedDestinationMeta.isAisle;
    if (isAisle && (minAisle == null || maxAisle == null || Number(minAisle) > Number(maxAisle))) {
      return;
    }
    setIsBoardUpdated(true);
    shiftAllocationDispatch({
      type: 'MOVE_ASSOCIATE',
      data: {
        associateAlias: associate.alias,
        from: {
          boardEntry: isBenched ? PROCESS_PATHS.BENCH : boardEntry.id,
          processPathId: assignedProcess
        },
        to: {
          boardEntry: toBoardEntry,
          processPath: toProcessPath,
          // Aisle destinations carry the user-selected span; the reducer expands min..max into aisles.
          ...(isAisle ? { minAisle: Number(minAisle), maxAisle: Number(maxAisle) } : {})
        }
      }
    });
    setSelectedDestination(null);
    setMinAisle(null);
    setMaxAisle(null);
    setOpenPopOver(false);
  }, [
    selectedDestination,
    selectedDestinationMeta,
    minAisle,
    maxAisle,
    associate,
    isBenched,
    boardEntry,
    assignedProcess,
    shiftAllocationDispatch,
    setIsBoardUpdated
  ]);

  return (
    <Column spacing="none" width={'60px'}>
      <Row spacing="none" alignmentHorizontal="center" width={'fit'}>
        <EditableLabel
          value={actualHeader ? actualHeader : ''}
          editable={Boolean(editableHeader)}
          onSave={onEditHeader}
          textType="B100"
        />
      </Row>

      <Row spacing="none">
        {associate.thumbnailBadge != null ? (
          <div
            className={'labor-board-employee-icon'}
            style={{ ...borderStyle, position: 'relative' }}
            onClick={onClickEmployeeIcon}
            ref={buttonRef}>
            {trackingBadge}
            <Badge
              badgeContent={associate.thumbnailBadge.text}
              color={associate.thumbnailBadge.color}
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
              <Thumbnail size="small" source={photoUrl} />
            </Badge>
          </div>
        ) : (
          <div
            className={'labor-board-employee-icon'}
            style={{ ...borderStyle, position: 'relative' }}
            onClick={onClickEmployeeIcon}
            ref={buttonRef}>
            {trackingBadge}
            <Thumbnail size="small" source={photoUrl} />
          </div>
        )}
      </Row>
      <Popover anchorNode={buttonRef.current} open={openPopOver} onClose={onClose} position="top">
        <Column spacing="300">
          <Row>
            <Column spacing="none" alignmentHorizontal="start">
              <Thumbnail size="small" source={photoUrl} />
            </Column>
            <Column spacing="none" alignmentHorizontal="start">
              <Row>
                <Text transform={'capitalize'} type={'h200'}>
                  {associate.name || ''}
                </Text>
              </Row>
              <Row>
                <Text type={'B200'} color={'secondary'}>
                  {`ID: ${associate.alias}`}
                </Text>
              </Row>
            </Column>
            <Column spacing="none" alignmentHorizontal="end">
              <Row>
                <Text type={'h200'}>{associate.ppr}</Text>
              </Row>
              <Row>
                <Text type={'B200'} color={'secondary'}>
                  PPR
                </Text>
              </Row>
            </Column>
          </Row>
          <ProcessPathListView processPathList={[assignedProcess]} title={'Assigned'} />
          <ProcessPathListView processPathList={associate.disallowedPaths} title={'Disallowed'} />
          <ProcessPathListView processPathList={associate.trainedPaths} title={'Trained'} />
          <ProcessPathListView processPathList={associate.recommendedPaths} title={'Suggested'} />
          {assignDestinations.length > 0 ? (
            <Column spacing="200">
              <Row spacing="200" alignmentVertical="bottom">
                <Select
                  value={selectedDestination}
                  onChange={onChangeDestination}
                  placeholder="Assign to…"
                  size="small"
                  width={220}>
                  {assignDestinations.map((dest) => (
                    <SelectOption key={dest.value} value={dest.value} label={dest.label} />
                  ))}
                </Select>
                <Button onClick={onClickAssign} type="primary" size="small" disabled={isAssignDisabled}>
                  Assign
                </Button>
              </Row>
              {selectedDestinationMeta && selectedDestinationMeta.isAisle && (
                <Row spacing="200" alignmentVertical="center">
                  <Text type="b200" color="secondary">
                    Aisles:
                  </Text>
                  {aisleOptions.length > 0 ? (
                    <React.Fragment>
                      <Select value={minAisle} onChange={setMinAisle} placeholder="From" size="small" width={90}>
                        {aisleOptions.map((aisleNo) => (
                          <SelectOption key={aisleNo} value={aisleNo} label={`${aisleNo}`} />
                        ))}
                      </Select>
                      <Text type="b200" color="secondary">
                        to
                      </Text>
                      <Select value={maxAisle} onChange={setMaxAisle} placeholder="To" size="small" width={90}>
                        {aisleOptions.map((aisleNo) => (
                          <SelectOption key={aisleNo} value={aisleNo} label={`${aisleNo}`} />
                        ))}
                      </Select>
                    </React.Fragment>
                  ) : (
                    <Text type="b200" color="secondary">
                      No aisles available
                    </Text>
                  )}
                </Row>
              )}
            </Column>
          ) : (
            <Row alignmentHorizontal="center">
              <Text type="b200" color="secondary">
                No eligible locations available
              </Text>
            </Row>
          )}
          <Row spacing="200" alignmentHorizontal="center">
            {associatePlacementIssueStatus.hasIssue && (
              <Text transform={'capitalize'} type="b200" color={'error'}>
                <Icon tokens={errorKnockoutIcon} /> {associatePlacementIssueStatus.reason}
              </Text>
            )}
          </Row>
          {associate.thumbnailBadge && (
            <Row>
              <div className={'employee-error-popup'}>
                <Icon tokens={infoKnockoutTokens} color="error" />
              </div>
              <Text>Not Processing packages for last 45 Minutes</Text>
            </Row>
          )}
          {!isBenched && (
            <Row alignmentHorizontal="center">
              {associate.thumbnailBadge && (
                <div className={'employee-error-popup-button'}>
                  <Button onClick={remove} type="link">
                    <Text color="inverted">Track into 5S</Text>
                  </Button>
                </div>
              )}
              {!associate.thumbnailBadge && (
                <Button onClick={remove} type="tertiary">
                  Remove
                </Button>
              )}
              <Button onClick={findReplacement} disabled={true}>
                Find a replacement
              </Button>
            </Row>
          )}
        </Column>
      </Popover>

      <Row spacing="none" alignmentHorizontal="center" width={'fit'}>
        <Text transform={'capitalize'} type="b100" truncate={'true'}>
          {associate.name ? associate.name.toLowerCase() : <span>&nbsp;</span>}
        </Text>
      </Row>
    </Column>
  );
};
